import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CaretLeft, CaretRight, ImageSquare } from '@phosphor-icons/react';
import { Button } from '../../../components/ui/button';
import { resolveMediaUrl } from '../../../utils/media';
import { colorWithAlpha, extractMediaPalette } from '../../../utils/mediaPalette';

export default function GuestHeroSlideshow({
  media = [],
  featuredPhotoUrl,
  displayNames,
  placeholderUrl = '/assets/logo.png',
  accentColor = '#D4AF37',
  surfaceColor = '#202020',
  onPaletteChange,
  onFeaturedPaletteChange,
}) {
  const visibleMedia = useMemo(
    () => [...media]
      .filter((item) => item?.visible !== false && item?.url)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [media]
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [featuredImageFailed, setFeaturedImageFailed] = useState(false);
  const featuredImageUrl = resolveMediaUrl(featuredPhotoUrl);
  const readPalette = useCallback((element, callback) => {
    if (!element || !callback) return;

    window.requestAnimationFrame(() => {
      const palette = extractMediaPalette(element);
      if (palette) callback(palette);
    });
  }, []);

  useEffect(() => {
    if (activeIndex >= visibleMedia.length) setActiveIndex(0);
  }, [activeIndex, visibleMedia.length]);

  useEffect(() => {
    setFeaturedImageFailed(false);
  }, [featuredImageUrl]);

  useEffect(() => {
    if (paused || visibleMedia.length <= 1) return undefined;
    const active = visibleMedia[activeIndex];
    if (active?.media_type === 'video') return undefined;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;
    const timer = window.setTimeout(
      () => setActiveIndex((current) => (current + 1) % visibleMedia.length),
      (active?.duration_seconds || 6) * 1000
    );
    return () => window.clearTimeout(timer);
  }, [activeIndex, paused, visibleMedia]);

  const goPrevious = () => {
    if (!visibleMedia.length) return;
    setActiveIndex((current) => (current - 1 + visibleMedia.length) % visibleMedia.length);
  };
  const goNext = () => {
    if (!visibleMedia.length) return;
    setActiveIndex((current) => (current + 1) % visibleMedia.length);
  };

  const active = visibleMedia[activeIndex];

  return (
    <div
      className="relative left-1/2 w-screen -translate-x-1/2 pb-16 md:left-auto md:mx-auto md:w-full md:max-w-[min(100%,calc((100svh-190px)*2))] md:translate-x-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div
        className="relative overflow-hidden border-y md:aspect-[16/8] md:rounded-[1.75rem] md:border"
        style={{
          backgroundColor: surfaceColor,
          borderColor: colorWithAlpha(accentColor, 0.34),
          boxShadow: `0 30px 80px rgba(0, 0, 0, 0.28), 0 0 0 1px ${colorWithAlpha(accentColor, 0.16)}`,
        }}
      >
        {active ? (
          active.media_type === 'video' ? (
            <video
              key={active.id}
              src={resolveMediaUrl(active.url, { preferProxy: true })}
              poster={resolveMediaUrl(active.poster_url)}
              className="relative z-10 block h-auto w-full md:absolute md:inset-0 md:h-full md:object-cover"
              autoPlay
              muted
              playsInline
              controls
              crossOrigin="anonymous"
              onLoadedData={(event) => readPalette(event.currentTarget, onPaletteChange)}
              onEnded={goNext}
              aria-label={active.caption || 'Celebration video'}
            />
          ) : (
            <img
              key={active.id}
              src={resolveMediaUrl(active.url, { preferProxy: true })}
              alt={active.caption || 'Celebration slideshow'}
              className="relative z-10 block h-auto w-full animate-in fade-in-0 duration-700 md:absolute md:inset-0 md:h-full md:object-cover"
              crossOrigin="anonymous"
              onLoad={(event) => readPalette(event.currentTarget, onPaletteChange)}
            />
          )
        ) : (
          <div
            className="flex aspect-video h-full flex-col items-center justify-center px-8 text-center text-white/80 md:aspect-auto"
            style={{ background: `radial-gradient(circle at top, ${colorWithAlpha(accentColor, 0.58)}, ${surfaceColor} 72%)` }}
          >
            <ImageSquare className="h-12 w-12 opacity-70" />
            <p className="mt-4 font-serif text-xl">Celebration memories will appear here</p>
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 z-20 bg-gradient-to-t from-black/35 via-transparent to-black/10" />
        <div className="pointer-events-none absolute inset-0 z-20 ring-1 ring-inset ring-white/20 md:rounded-[1.7rem]" />

        {visibleMedia.length > 1 && (
          <>
            <Button
              type="button"
              size="icon"
              variant="secondary"
              onClick={goPrevious}
              className="absolute left-3 top-1/2 z-30 h-10 w-10 -translate-y-1/2 rounded-full border border-white/30 bg-black/30 text-white backdrop-blur hover:bg-black/50"
              aria-label="Previous slide"
            >
              <CaretLeft className="h-5 w-5" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="secondary"
              onClick={goNext}
              className="absolute right-3 top-1/2 z-30 h-10 w-10 -translate-y-1/2 rounded-full border border-white/30 bg-black/30 text-white backdrop-blur hover:bg-black/50"
              aria-label="Next slide"
            >
              <CaretRight className="h-5 w-5" />
            </Button>
            <div className="absolute bottom-4 left-1/2 z-30 flex -translate-x-1/2 gap-2">
              {visibleMedia.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`h-2.5 rounded-full border border-white/60 transition-all ${
                    index === activeIndex ? 'w-8 bg-white' : 'w-2.5 bg-white/35'
                  }`}
                  aria-label={`Show slide ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="absolute bottom-0 left-5 z-40 w-[34%] min-w-[120px] max-w-[180px] md:left-8 md:w-[27%] md:min-w-[110px] md:max-w-[210px]">
        <div
          className="aspect-[4/5] overflow-hidden rounded-[1.25rem] border-4 bg-card shadow-[0_16px_35px_rgba(0,0,0,0.24)]"
          style={{ borderColor: `${accentColor}DD` }}
        >
          <img
            src={!featuredImageFailed && featuredImageUrl
              ? featuredImageUrl
              : placeholderUrl}
            alt={displayNames}
            className={`h-full w-full ${
              featuredPhotoUrl && !featuredImageFailed ? 'object-cover' : 'object-contain p-2'
            }`}
            onLoad={(event) => readPalette(event.currentTarget, onFeaturedPaletteChange)}
            onError={() => setFeaturedImageFailed(true)}
          />
        </div>
      </div>
    </div>
  );
}
